import { useApolloClient } from '@apollo/client/react'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'

import {
  clearBrowserImpersonationSession,
  configureSilentRenewForOidcUser,
} from '@/auth/keycloakExchangeSession'
import { getKeycloakUserManager } from '@/auth/keycloakUserManager'
import {
  clearPreImpersonationOidcSnapshot,
  readPreImpersonationOidcSnapshot,
} from '@/auth/preImpersonationOidcSnapshot'
import { useAuth } from '@/auth/useAuth'

function displayNameFromOidcProfile(
  profile:
    | { name?: string; preferred_username?: string; email?: string }
    | undefined
): string {
  if (!profile) {
    return ''
  }
  if (typeof profile.name === 'string' && profile.name) {
    return profile.name
  }
  if (
    typeof profile.preferred_username === 'string' &&
    profile.preferred_username
  ) {
    return profile.preferred_username
  }
  if (typeof profile.email === 'string' && profile.email) {
    return profile.email
  }
  return ''
}

export const useEndImpersonation = () => {
  const { t } = useTranslation()
  const apolloClient = useApolloClient()
  const { signout, refreshSession } = useAuth()

  const endImpersonation = useCallback(async () => {
    const snapshot = readPreImpersonationOidcSnapshot()

    const fallbackFullSignOut = async (
      reason: 'no_snapshot' | 'restore_failed'
    ) => {
      clearBrowserImpersonationSession()
      clearPreImpersonationOidcSnapshot()
      await apolloClient.clearStore().catch(() => {})
      toast.info(
        t(
          reason === 'no_snapshot'
            ? 'admin:accountManagement.users.actions.endImpersonation.noSnapshotFallback'
            : 'admin:accountManagement.users.actions.endImpersonation.restoreFailed',
          {
            defaultValue:
              reason === 'no_snapshot'
                ? 'No saved administrator session for this tab. Signing out completely.'
                : 'Could not restore your administrator session. Signing out completely.',
          }
        ),
        { autoClose: 7000 }
      )
      await signout()
    }

    if (!snapshot?.access_token) {
      await fallbackFullSignOut('no_snapshot')
      return
    }

    try {
      clearBrowserImpersonationSession()
      await apolloClient.clearStore().catch(() => {})
      const um = getKeycloakUserManager()
      await um.storeUser(snapshot)
      await um.events.load(snapshot)
      clearPreImpersonationOidcSnapshot()
      configureSilentRenewForOidcUser(snapshot)
      await refreshSession()

      const name = displayNameFromOidcProfile(snapshot.profile)
      toast.success(
        name
          ? t(
              'admin:accountManagement.users.actions.endImpersonation.success',
              {
                name,
              }
            )
          : t(
              'admin:accountManagement.users.actions.endImpersonation.successDefault',
              {
                defaultValue: 'Returned to your administrator session.',
              }
            )
      )
    } catch (error) {
      console.error('Error ending impersonation (restore):', error)
      await fallbackFullSignOut('restore_failed')
    }
  }, [t, apolloClient, signout, refreshSession])

  return {
    endImpersonation,
  }
}
